import { type FC, useState, useEffect } from 'react';
import { supabase } from '@/lib/api/supabase';
import { dbToSponsor } from '@/types';
import type { Sponsor } from '@/types';

/**
 * Sponsor gallery component with continuous marquee scroll
 * Displays sponsor logos on the homepage
 */
export const SponsorGallery: FC = () => {
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const { data, error } = await supabase
          .from('sponsors')
          .select('*')
          .eq('visible', true)
          .eq('type', 'sponsor')
          .not('logo_url', 'is', null)
          .order('display_order', { ascending: true });

        if (error) {
          console.error('Failed to fetch sponsors:', error);
          return;
        }

        if (data) {
          setSponsors(data.map(dbToSponsor));
        }
      } catch (err) {
        console.error('Error fetching sponsors:', err);
      } finally {
        setLoading(false);
      }
    };

    void fetchSponsors();
  }, []);

  if (loading || sponsors.length === 0) {
    return null;
  }

  // Speed: pixels per second
  const speed = 40;
  // Each item is w-48 (192px) + gap of 48px (gap-12) = 240px per item
  const itemWidth = 240;
  const totalWidth = sponsors.length * itemWidth;
  const duration = totalWidth / speed;

  return (
    <div className="bg-white/10 backdrop-blur-sm py-12">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-viking text-white text-center mb-8">
          Our Sponsors
        </h2>

        <div className="relative overflow-hidden">
          <div
            className="sponsor-marquee flex gap-12 items-center"
            onMouseEnter={(e) => e.currentTarget.style.animationPlayState = 'paused'}
            onMouseLeave={(e) => e.currentTarget.style.animationPlayState = 'running'}
          >
            {/* Render sponsors twice for seamless looping */}
            {[0, 1].map((copy) =>
              sponsors.map((sponsor, index) => (
                <div
                  key={`${sponsor.id}-copy${copy}-${index}`}
                  className="flex-shrink-0 w-48 h-24 flex items-center justify-center bg-white rounded-lg p-4 shadow-md"
                >
                  <img
                    src={sponsor.logoUrl || ''}
                    alt={sponsor.name}
                    className="max-w-full max-h-full object-contain"
                    loading="lazy"
                  />
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <style>{`
        .sponsor-marquee {
          animation: sponsor-scroll ${duration}s linear infinite;
        }

        @keyframes sponsor-scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-${totalWidth}px);
          }
        }
      `}</style>
    </div>
  );
};
